import React from 'react';
import PropTypes from 'prop-types';
import DOMPurify from 'dompurify';
import classNames from 'classnames';
import StaticBuilderFormattedFields from 'StaticBuilder/StaticBuilderFormattedFields';
import StaticBuilderForm from 'StaticBuilder/StaticBuilderForm';
import StaticBuilderSaveProgressBar from 'StaticBuilder/StaticBuilderSaveProgressBar';

const BUTTON_SELECTOR = '.controls button';
const INPUT_SELECTOR = '.controls input';
const SELECT_SELECTOR = '.controls select';
const TEXTAREA_SELECTOR = '.controls textarea';
const FIELDSET_SELECTOR = 'fieldset .control-group';

class StaticBuilderSection extends React.Component {
  constructor(props) {
    super(props);

    this.inlineConditionals = this.inlineConditionals.bind(this);
    this.onNewSectionLoaded = this.onNewSectionLoaded.bind(this);
    this.onKeyPress = this.onKeyPress.bind(this);
    this.setSidebarShiftAmount = this.setSidebarShiftAmount.bind(this);
    this.calculateSidebarShiftAmount = this.calculateSidebarShiftAmount.bind(this);

    this.state = {
      sidebarShiftAmount: 0,
    };
  }

  componentDidMount() {
    if (this.props.showSidebarAnimation) {
      window.addEventListener('resize', this.setSidebarShiftAmount);
      this.setSidebarShiftAmount();
    }
  }

  componentDidUpdate(prevProps) {
    // new section loaded
    if (prevProps.isLoading && !this.props.isLoading) {
      this.onNewSectionLoaded();
    }
  }

  // Capture the Enter button and treat it as Save and continue
  onKeyPress(e) {
    // Does not call for self serve because saveOnEnter fires off static builder actions
    // Self serve enter key functionality handled in StaticBuilderForm

    if (this.props.isSelfServe) {
      return;
    }

    if (!this.props.isMobileOptimization) {
      window.SessionManager.saveOnEnter(e);
    } else if (e.key === 'Enter') {
      // Handles enter press for mobile optimization split test
      const formInputs = document.querySelectorAll(
        `${INPUT_SELECTOR}, ${SELECT_SELECTOR}, ${TEXTAREA_SELECTOR}`,
      );

      const activeIndex = [...formInputs].findIndex(formInput => (
        formInput === document.activeElement
      ));

      if (activeIndex === formInputs.length - 1) {
        window.SessionManager.saveOnEnter(e);
      } else {
        formInputs[activeIndex + 1].focus();
      }
    }
  }

  onNewSectionLoaded() {
    window.scrollTo(0, 0);

    if (this.props.onSectionLoadActions.length) {
      this.props.onSectionLoadActions.forEach((onSectionLoadAction) => {
        onSectionLoadAction();
      });
    }

    const formInputs = document.querySelectorAll(
      `${INPUT_SELECTOR}, ${SELECT_SELECTOR}, ${TEXTAREA_SELECTOR}, ${BUTTON_SELECTOR}`,
    );

    if (formInputs.length) {
      formInputs[0].focus();
    }
  }

  setSidebarShiftAmount() {
    this.setState({
      sidebarShiftAmount: this.calculateSidebarShiftAmount(),
    });
  }

  calculateSidebarShiftAmount() {
    const browserWidth = this.props.hasSiteSidebar ? window.innerWidth - 48 : window.innerWidth;
    if (browserWidth >= 1260 && browserWidth <= 1500) {
      const sidebarShiftAmount = ((1500 - browserWidth) / 2) + 2;
      return sidebarShiftAmount;
    }
    return 0;
  }

  inlineConditionals() {
    if (this.props.disableConditionals) {
      return;
    }
    // Yikes! We currently have dynamically produced jquery from our php class,
    // `Conditionals`. In order to reuse our StaticBuilder react component, we
    // need to include these inline scripts while we look for other solutions that
    // will make use of our redux store
    const inlineScriptsWithoutTag = this.props.inlineConditionalScripts.replace(
      /<script.+?>/g,
      '',
    ).replace(
      /<script>/g,
      '',
    )
    .replace(
      /<\/script>/g,
      '',
    );

    eval(
      DOMPurify.sanitize(
        inlineScriptsWithoutTag,
        { SAFE_FOR_JQUERY: true },
      ),
    );

    const fields = document.querySelectorAll(FIELDSET_SELECTOR);
    const visibleFields = [...fields].filter(field => (
      field.offsetParent !== null
    ));

    if (visibleFields.length === 0) {
      // Check if we can go to the next step (or if it's the last section)
      const skipPage = true;
      const nextStepAvailable = this.props.loadNextStaticBuilderSection(skipPage);

      if (!nextStepAvailable) {
        this.props.saveAndContinue();
      }
    }

    if (window.secondPresence) {
      setTimeout(window.Wizard.interface.updateSectionProgressTimed, 10);
    }
  }

  render() {
    const {
      documentName,
      highlightedBox,
      isLoading,
      showHelpSideBar,
      showSidebarAnimation,
    } = this.props;
    const sidebarShiftAmount = showHelpSideBar ? `${this.state.sidebarShiftAmount}px` : '0';

    let screenStyle = {
      marginLeft: sidebarShiftAmount,
      opacity: 1.0,
    };

    if (isLoading) {
      screenStyle = {
        ...screenStyle,
        opacity: 0.5,
        height: 30,
      };
    }

    const screenClass = classNames(
      'bpage',
      {
        'screen-with-transition': showSidebarAnimation,
      },
    );

    const screenOverlayClass = classNames(
      'screen-overlay',
      screenClass,
    );

    const highlightedImage = `url(/styles/custom-document/assets/${documentName}-highlighted-boxes/box-${highlightedBox}.png)`;
    const backgroundImage = documentName && highlightedBox ? highlightedImage : 'none';

    return (
      <React.Fragment>
        <div className="hide">
          <h4>Loading Next Section...</h4>
          <div className="progressAnimation">
            <span className="bar" />
          </div>
        </div>
        <div className="clear" />
        <div
          className={screenOverlayClass}
          style={{
            backgroundImage,
            marginLeft: sidebarShiftAmount,
          }}
        />
        <div
          id="screen"
          className={screenClass}
          onKeyPress={this.onKeyPress}
          style={screenStyle}
        >
          {
            isLoading && (
              <h3 className="static-builder-form-header-loading">Loading...</h3>
            )
          }
          {
            !isLoading && (
              <React.Fragment>
                { this.props.renderHeaderTextAsHtml ?
                  <h2
                    className="static-builder-form-header"
                    dangerouslySetInnerHTML={{ __html: this.props.header }}
                  />
                : <h2 className="static-builder-form-header">
                  {this.props.header}
                </h2>
                 }
                <div id="staticBuilderFormContainer" className="innerContent">
                  <StaticBuilderForm
                    loadPreviousStaticBuilderSection={this.props.loadPreviousStaticBuilderSection}
                    saveAndSign={this.props.saveAndSign}
                    saveAndContinue={this.props.saveAndContinue}
                    renderHeaderTextAsHtml={this.props.renderHeaderTextAsHtml}
                    shareKey={this.props.shareKey}
                    onStep={this.props.onStep}
                    sectionKeys={this.props.sectionKeys}
                    documentType={this.props.documentType}
                    finishSelfServe={this.props.finishSelfServe}
                    formattedFields={this.props.formattedFields}
                    inlineConditionals={this.inlineConditionals}
                    isSelfServe={this.props.isSelfServe}
                    isPreviewMode={this.props.isPreviewMode}
                    showRequestSignature={this.props.showRequestSignature}
                  />
                </div>

                <StaticBuilderSaveProgressBar />

              </React.Fragment>
            )
          }
        </div>

      </React.Fragment>
    );
  }
}

StaticBuilderSection.defaultProps = {
  disableConditionals: false,
  documentName: '',
  finishSelfServe: () => {},
  hasSiteSidebar: false,
  header: '',
  highlightedBox: '',
  inlineConditionalScripts: '',
  isMobileOptimization: false,
  isSelfServe: false,
  isPreviewMode: false,
  onSectionLoadActions: [],
  onStep: 0,
  renderHeaderTextAsHtml: false,
  sectionKeys: [],
  shareKey: '',
  showHelpSideBar: false,
  showSidebarAnimation: false,
  showRequestSignature: true,
};

StaticBuilderSection.propTypes = {
  disableConditionals: PropTypes.bool,
  documentName: PropTypes.string,
  documentType: PropTypes.string.isRequired,
  finishSelfServe: PropTypes.func,
  formattedFields: PropTypes.instanceOf(StaticBuilderFormattedFields).isRequired,
  hasSiteSidebar: PropTypes.bool,
  header: PropTypes.string,
  highlightedBox: PropTypes.string,
  inlineConditionalScripts: PropTypes.string,
  isLoading: PropTypes.bool.isRequired,
  isMobileOptimization: PropTypes.bool,
  isSelfServe: PropTypes.bool,
  isPreviewMode: PropTypes.bool,
  loadNextStaticBuilderSection: PropTypes.func.isRequired,
  loadPreviousStaticBuilderSection: PropTypes.func.isRequired,
  renderHeaderTextAsHtml: PropTypes.bool,
  saveAndSign: PropTypes.func.isRequired,
  showHelpSideBar: PropTypes.bool,
  showSidebarAnimation: PropTypes.bool,
  showRequestSignature: PropTypes.bool,
  onSectionLoadActions: PropTypes.arrayOf(PropTypes.func),
  onStep: PropTypes.number,
  saveAndContinue: PropTypes.func.isRequired,
  sectionKeys: PropTypes.arrayOf(PropTypes.string),
  shareKey: PropTypes.string,
};

export default StaticBuilderSection;



// WEBPACK FOOTER //
// ./src-react/StaticBuilder/StaticBuilderSection/index.jsx